import { Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import ErrorScreen from '~/components/ErrorScreen';
import LoadingScreen from '~/components/LoadingScreen';
import Page from '~/components/Page';
import { PdfViewer } from '~/components/PdfViewer';
import IconButton from '~/components/buttons/NewIconButton';
import CircleSpinner from '~/components/data-display/CircleSpinner/CircleSpinner';
import { SkeletonLoader } from '~/components/data-display/SkeletonLoader/SkeletonLoader';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import IconArrowLeft from '~/icons/icon-arrow-left.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { usePayrollDownloadUrlQuery, useDocumentQuery } from '~/services/document';
import { usePayrollBookkeepingProposalQuery } from '~/services/payroll';
import { DocumentWithAttachments } from '~/types/document';
import { DocumentAttachment } from '~/types/document-attachment';
import BookkeepingTransactionForm from '~/views/bookkeeping/transactions/BookkeepingTransactionForm';
import { PayrollLoadingModal } from './PayrollLoadingModal';
import styles from './PayrollRegistration.module.scss';

const PROPOSAL_TIMEOUT_MS = 35_000;

const isPdf = (...values: (string | undefined)[]) => values.some((value) => !!value && /\.pdf(\?|$)/i.test(value));

const DocumentPreview = ({
  attachment,
  url,
  isLoading,
}: {
  attachment: DocumentAttachment | undefined;
  url?: string;
  isLoading: boolean;
}) => {
  const { t } = useTranslation('bookkeeping', {
    keyPrefix: 'general_bookkeeping.non_electronic_documents.payroll_registration',
  });
  const [height, setHeight] = useState(0);

  const measurePreview = useCallback((element: HTMLDivElement | null) => {
    if (element) setHeight(element.offsetHeight);
  }, []);

  if (isLoading)
    return (
      <div className={styles.preview}>
        <CircleSpinner style={{ color: 'white' }} />
      </div>
    );

  if (!attachment && !url) {
    return (
      <div className={styles.preview}>
        <Text variant="body1" weight="regular" className={styles.noPreview}>
          {t('no_preview')}
        </Text>
      </div>
    );
  }

  const displayUrl = url ?? attachment?.path;

  return (
    <div className={styles.preview} ref={measurePreview}>
      {isPdf(displayUrl, attachment?.filename, attachment?.path) ? (
        <PdfViewer file={displayUrl} height={height || undefined} />
      ) : (
        <img src={displayUrl} alt={attachment?.filename ?? 'file'} className={styles.previewImage} />
      )}
    </div>
  );
};

const PayrollRegistrationContent = ({ document }: { document: DocumentWithAttachments }) => {
  const { t } = useTranslation('bookkeeping', {
    keyPrefix: 'general_bookkeeping.non_electronic_documents.payroll_registration',
  });
  const navigate = useNavigate();
  const { data: previewUrl, isLoading } = usePayrollDownloadUrlQuery(document.id);

  const handleDone = () => navigate(RouteConfig.GeneralBookkeepingNonElectronicDocuments.buildLink());

  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), PROPOSAL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const { data: proposal = null } = usePayrollBookkeepingProposalQuery(document.id, { enabled: !timedOut });

  const [modalCompleted, setModalCompleted] = useState(false);
  const showLoadingModal = !timedOut && !modalCompleted;
  const proposalUnavailable = timedOut && !proposal;

  useEffect(() => {
    if (proposalUnavailable) snack.error(t('proposal_error'));
  }, [proposalUnavailable, t]);

  return (
    <div className={styles.layout}>
      <DocumentPreview isLoading={isLoading} attachment={document.attachments[0]} url={previewUrl} />

      <VStack gap={3} className={styles.formPanel}>
        <HStack gap={2} align="center">
          <IconButton size="large" variant="ghost" Icon={IconArrowLeft} onClick={() => navigate(-1)} />
          <Text variant="title4" weight="light">
            {t('title')}
          </Text>
        </HStack>
        <Suspense fallback={<LoadingSkeleton />}>
          {showLoadingModal ? (
            <>
              <PayrollLoadingModal
                fileName={document.attachments[0]?.filename ?? ''}
                isComplete={!!proposal}
                onComplete={() => setModalCompleted(true)}
              />
              <LoadingSkeleton />
            </>
          ) : (
            <BookkeepingTransactionForm
              transactionIds={[]}
              disableSaveAsTemplate={true}
              bookkeepingTransactionId={null}
              recommendedReconciliationFlowId={null}
              proposal={proposal}
              onSuccess={handleDone}
              onClose={handleDone}
            />
          )}
        </Suspense>
      </VStack>
    </div>
  );
};

const PayrollRegistration: React.FunctionComponent = () => {
  const { t } = useTranslation('bookkeeping', {
    keyPrefix: 'general_bookkeeping.non_electronic_documents.payroll_registration',
  });
  const companyId = useActiveCompanyId();

  const { documentId } = useParams<{ documentId: string }>();
  const { data: document, status } = useDocumentQuery(documentId);

  const isWrongCompany = document && document.company.id !== companyId;
  if (!documentId || isWrongCompany) {
    return <Navigate to={RouteConfig.GeneralBookkeepingNonElectronicDocuments.buildLink()} replace />;
  }

  if (status === 'pending') {
    return (
      <Page title={t('title')}>
        <LoadingScreen />
      </Page>
    );
  }

  if (status === 'error') {
    return (
      <Page title={t('title')}>
        <ErrorScreen />
      </Page>
    );
  }

  return (
    <Page title={t('title')} className={styles.root}>
      <PayrollRegistrationContent document={document} />
    </Page>
  );
};

const LoadingSkeleton = () => (
  <>
    <SkeletonLoader height={33} />
    <SkeletonLoader height={63} />
    <SkeletonLoader height={33} />
    <SkeletonLoader height={33} />
    <SkeletonLoader height={33} />
  </>
);

export default PayrollRegistration;
